import { useEffect, RefObject } from 'react'

type UseTextareaHeightProps = {
  textareaRef: RefObject<HTMLTextAreaElement>
  value: string
  maxLines?: number
  minLines?: number
  state?: string
}

export const useTextareaHeight = ({
  textareaRef,
  value,
  maxLines = 3,
  minLines = 1,
  state = null,
}: UseTextareaHeightProps) => {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const textarea = textareaRef.current
    if (!textarea) return

    // Сохраняем пользовательские стили для восстановления
    const originalStyles = {
      height: textarea.style.height,
      overflow: textarea.style.overflow,
      boxSizing: textarea.style.boxSizing,
      rows: textarea.rows,
    }

    // Устанавливаем временные стили для точных измерений
    textarea.style.boxSizing = 'border-box'
    textarea.style.height = 'auto'
    textarea.style.overflow = 'hidden'
    textarea.style.resize = 'none'
    textarea.rows = minLines

    // Получаем вычисленные стили
    const computedStyle = window.getComputedStyle(textarea)

    // Расчет lineHeight
    const getLineHeight = () => {
      const temp = document.createElement('div')
      temp.style.font = computedStyle.font
      temp.style.lineHeight = computedStyle.lineHeight
      temp.innerHTML = 'A'
      document.body.appendChild(temp)
      const height = temp.clientHeight
      document.body.removeChild(temp)
      return height || parseInt(computedStyle.fontSize) * 1.2
    }

    const lineHeight = getLineHeight()
    const paddingTop = parseFloat(computedStyle.paddingTop)
    const paddingBottom = parseFloat(computedStyle.paddingBottom)

    // Вычисляем высоту контента
    const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom

    // Рассчитываем количество строк
    const lines = Math.ceil(contentHeight / lineHeight)
    const clampedLines = Math.min(Math.max(minLines, lines), maxLines)

    // Устанавливаем финальные размеры
    const totalPadding = paddingTop + paddingBottom

    textarea.style.height = `${clampedLines * lineHeight + totalPadding}px`
    textarea.style.maxHeight = `${clampedLines * lineHeight + totalPadding}px`
    textarea.style.overflow = clampedLines >= maxLines ? 'auto' : 'hidden'
    textarea.rows = clampedLines

    // Восстанавливаем оригинальные стили при размонтировании
    return () => {
      if (textarea) {
        textarea.style.height = originalStyles.height
        textarea.style.overflow = originalStyles.overflow
        textarea.style.boxSizing = originalStyles.boxSizing
        textarea.rows = originalStyles.rows
      }
    }
  }, [value, textareaRef, maxLines, minLines, state])
}
